import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  TrendingUp, LayoutDashboard, Users, ArrowDownToLine, Receipt,
  LogOut, Menu, Shield, ChevronRight, Award, QrCode, Bell,
  Wallet, X, BarChart3, Bitcoin, PiggyBank, MessageCircle, Gift
} from 'lucide-react';

const navItems = [
  { path: '/admin',                  icon: LayoutDashboard, label: 'Dashboard',         exact: true },
  { path: '/admin/users',            icon: Users,           label: 'Users' },
  { path: '/admin/tickets',          icon: MessageCircle,   label: 'Support Tickets' },
  { path: '/admin/business',         icon: BarChart3,       label: 'Business Stats' },
  { path: '/admin/profits',          icon: TrendingUp,      label: 'Daily Profits' },
  { path: '/admin/withdrawals',      icon: ArrowDownToLine, label: 'Withdrawals' },
  { path: '/admin/wallet-requests',  icon: Wallet,          label: 'Wallet Requests' },
  { path: '/admin/transactions',     icon: Receipt,         label: 'Transactions' },
  { path: '/admin/ranks',            icon: Award,           label: 'Ranks & Rewards' },
  { path: '/admin/investor-packages',icon: Bitcoin,         label: 'Investor Packages' },
  { path: '/admin/fund',             icon: PiggyBank,       label: 'Fund Management' },
  { path: '/admin/payment-config',   icon: QrCode,          label: 'Payment Config' },
  { path: '/admin/offer',            icon: Gift,            label: 'Offer Popup' },
];

export default function AdminLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [showNotif, setShowNotif] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 20000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get('/api/admin/notifications');
      setNotifications(data.notifications || []);
      setUnread(data.unreadCount || 0);
    } catch (e) {}
  };

  const markAllRead = async () => {
    await axios.put('/api/admin/notifications/read-all').catch(() => {});
    setUnread(0);
    setNotifications(n => n.map(x => ({ ...x, isRead: true })));
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const Sidebar = ({ mobile = false }) => (
    <div className={`${mobile ? 'flex flex-col h-full' : 'hidden lg:flex flex-col h-screen sticky top-0'} w-64 bg-dark-800 border-r border-white/5`}>
      <div className="p-6 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-bold text-white">UniqueAiTarde</p>
            <p className="text-xs text-red-400">Admin Panel</p>
          </div>
        </div>
      </div>

      <div className="p-4 border-b border-white/5">
        <div className="bg-dark-600 rounded-xl p-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 font-bold text-sm flex-shrink-0">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-white font-medium text-sm truncate">{user?.name}</p>
            <p className="text-red-400 text-xs">Administrator</p>
          </div>
          {unread > 0 && (
            <span className="ml-auto bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold">
              {unread}
            </span>
          )}
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map(({ path, icon: Icon, label, exact }) => {
          const active = exact ? location.pathname === path : location.pathname.startsWith(path);
          return (
            <Link key={path} to={path} onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
                ${active ? 'bg-red-500/20 text-red-300 border border-red-500/30' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <Icon className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1">{label}</span>
              {active && <ChevronRight className="w-4 h-4" />}
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-white/5">
        <Link to="/dashboard"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-all mb-2">
          <TrendingUp className="w-5 h-5" /> User View
        </Link>
        <button onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all">
          <LogOut className="w-5 h-5" /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-dark-900 flex">
      <Sidebar />
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          <div className="relative z-10 w-64 h-full"><Sidebar mobile /></div>
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="sticky top-0 z-40 bg-dark-800 border-b border-white/5 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} className="text-gray-400 hover:text-white lg:hidden">
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2 lg:hidden">
            <Shield className="w-5 h-5 text-red-500" />
            <span className="font-bold text-white">Admin</span>
          </div>
          <div className="hidden lg:block" />

          <div className="relative">
            <button
              onClick={() => { setShowNotif(!showNotif); if (unread > 0) markAllRead(); }}
              className="relative p-2 text-gray-400 hover:text-white rounded-xl hover:bg-white/5 transition-colors">
              <Bell className="w-5 h-5" />
              {unread > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
            </button>

            {showNotif && (
              <div className="absolute right-0 top-12 w-80 bg-dark-700 border border-white/10 rounded-2xl shadow-2xl z-50 overflow-hidden">
                <div className="p-4 border-b border-white/5 flex items-center justify-between">
                  <p className="text-white font-semibold">Notifications</p>
                  <button onClick={() => setShowNotif(false)} className="text-gray-400 hover:text-white">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="max-h-96 overflow-y-auto divide-y divide-white/5">
                  {notifications.length === 0 ? (
                    <p className="text-center text-gray-500 py-8 text-sm">No notifications</p>
                  ) : notifications.map(n => (
                    <div key={n._id} className={`px-4 py-3 hover:bg-white/2 cursor-pointer ${!n.isRead ? 'bg-red-500/5 border-l-2 border-l-red-500' : ''}`}>
                      <p className="text-white text-sm font-medium">{n.title}</p>
                      <p className="text-gray-400 text-xs mt-0.5 leading-relaxed">{n.message}</p>
                      {n.relatedUser && (
                        <Link to={`/admin/users/${n.relatedUser._id || n.relatedUser}`}
                          className="text-primary-400 text-xs mt-1 hover:underline inline-block">
                          View User →
                        </Link>
                      )}
                      <p className="text-gray-600 text-xs mt-1">
                        {new Date(n.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {showNotif && <div className="fixed inset-0 z-30" onClick={() => setShowNotif(false)} />}
        <main className="p-4 lg:p-8 max-w-7xl mx-auto">{children}</main>
      </div>
    </div>
  );
}
